import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { useAtom } from 'jotai'
import {
  type FC,
  type ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { TableActions } from '@/components/elements'
import {
  DESCRIPTIONS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { type Sorter } from '@/features/shared/models'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { bulkUpdateDeliveryStatus } from '@/features/shipment-orders/api'
import { ShipmentOrderEditModal } from '@/features/shipment-orders/components'
import { bundleOrdersQueryOptions } from '@/features/shipment-orders/hooks'
import {
  type ShipmentOrdersListExEntity,
  type PageEntity,
  type UserMeExEntity
} from '@/libs/api'
import { type ShipmentDeliveryStatusType } from '@/libs/api/types'

import {
  addOrdersToSelection,
  buildBundleSelectionStates,
  mergeSelectedOrders,
  refreshSelectedOrders,
  removeBundleFromSelection,
  splitPinnedOrders,
  type ShipmentOrdersSearch
} from '../../-models'
import { type ShipmentOrdersFormValues } from '../../-models/shipment-orders-form-values'
import {
  initialShipmentOrdersSelection,
  shipmentOrdersSelectionAtom
} from '../../-states'
import { ShipmentOrdersTableBody } from '../ShipmentOrdersTableBody/ShipmentOrdersTableBody'
import { ShipmentOrdersTableHeaderActions } from '../ShipmentOrdersTableHeaderActions/ShipmentOrdersTableHeaderActions'

/** 選択中に表示される一括操作バーの高さ(TableActions の min-h-10 = 40px) */
const SELECTION_OVERLAY_HEIGHT = 40

type Props = ComponentPropsWithoutRef<'div'> & {
  tenantKey: string
  authUser: UserMeExEntity
  shipmentOrders: ShipmentOrdersListExEntity[]
  pagination: PageEntity | null
  search: ShipmentOrdersSearch
  onChangePage: (page: number, pageSize: number) => void
  onChangeSort: (sorter: Sorter) => void
  onDataRefresh?: () => Promise<void>
}

/**
 * 配送依頼一覧のテーブル
 */
export const ShipmentOrdersTable: FC<Props> = ({
  tenantKey,
  authUser,
  shipmentOrders,
  pagination,
  search,
  onChangePage,
  onChangeSort,
  onDataRefresh,
  ...props
}) => {
  // 選択行 entity 配列を source of truth にし、ページング・ソート・絞り込みを跨いで選択を維持する。
  // ページング・ソート・検索条件の変更は TanStack Router の matchId が変わるため
  // ShipmentOrdersTable はその都度再マウントされる。useState では再マウントのたびに選択が
  // 消えてしまうため、再マウントを跨いで生き残るモジュールスコープの atom に保持する
  const [selection, setSelection] = useAtom(shipmentOrdersSelectionAtom)

  // atom の選択は「保存時点の拠点・テナント」を伴って保持されている。現在の文脈と一致しない
  // 場合はテナント・拠点をまたいだ持ち越しになってしまうため、選択なし(空)として扱う
  const isSelectionContextCurrent =
    selection.dcId === search.dcId && selection.tenantKey === tenantKey
  const selectedOrders = useMemo(
    () => (isSelectionContextCurrent ? selection.orders : []),
    [isSelectionContextCurrent, selection.orders]
  )

  const [isEditing, setIdEditing] = useState<boolean>(false)
  // 配送依頼No クリックで開く編集モーダルの対象依頼
  const [editingShipmentOrder, setEditingShipmentOrder] =
    useState<ShipmentOrdersListExEntity | null>(null)
  const methods = useForm<ShipmentOrdersFormValues>({ mode: 'onChange' })

  // isSelectionContextCurrent による読み出し側のマスクだけでは atom の中身が残ったままになり、
  // 元の拠点・テナントに戻ったときに消えたはずの選択が復活してしまう(誤操作防止という目的を
  // 果たせない)。不一致と判明した時点で atom 自体を初期状態に戻す。
  // 既に初期状態(同一参照)なら set 自体が無駄になるためスキップし、無駄な set を避ける
  useEffect(() => {
    if (isSelectionContextCurrent) {
      return
    }
    if (selection !== initialShipmentOrdersSelection) {
      setSelection(initialShipmentOrdersSelection)
    }
  }, [isSelectionContextCurrent, selection, setSelection])

  const selectedRowKeys = useMemo(
    () => selectedOrders.map((order) => order.id),
    [selectedOrders]
  )

  // 選択済みだが現ページ結果に無い行をテーブル先頭にピン留めするための dataSource を組み立てる
  const { pinnedOrders, dataSource } = useMemo(
    () => splitPinnedOrders(selectedOrders, shipmentOrders),
    [selectedOrders, shipmentOrders]
  )

  const pinnedRowIds = useMemo(
    () => new Set(pinnedOrders.map((order) => order.id)),
    [pinnedOrders]
  )

  const handleSelectionChange = useCallback(
    (keys: React.Key[], rows: (ShipmentOrdersListExEntity | undefined)[]) => {
      // 選択を更新するタイミングで現在の文脈(拠点・テナント)を必ず書き込み直す。
      // これにより次回参照時の突き合わせが常に成立する
      setSelection((prev) => ({
        dcId: search.dcId,
        tenantKey,
        orders: mergeSelectedOrders(
          prev.dcId === search.dcId && prev.tenantKey === tenantKey
            ? prev.orders
            : [],
          keys,
          rows
        )
      }))
    },
    [search.dcId, tenantKey, setSelection]
  )

  const clearSelection = useCallback(
    () => setSelection(initialShipmentOrdersSelection),
    [setSelection]
  )

  // サーバー再取得のたびに選択キャッシュの鮮度を補正する。差し替えが無い場合は同一参照が
  // 返るため setState が実質的な no-op になり、無限ループにはならない
  useEffect(() => {
    setSelection((prev) => {
      // 文脈が現在と異なる(=表示されていない)選択はここでは補正しない
      if (prev.dcId !== search.dcId || prev.tenantKey !== tenantKey) {
        return prev
      }
      const refreshed = refreshSelectedOrders(prev.orders, shipmentOrders)
      return refreshed === prev.orders ? prev : { ...prev, orders: refreshed }
    })
  }, [shipmentOrders, search.dcId, tenantKey, setSelection])

  const queryClient = useQueryClient()

  // まとめキー左のチェック: 同じまとめキーの依頼をまとめて行選択する。
  // 画面に出ているメンバーが全て選択済みなら解除、それ以外（未選択・一部選択）は全件選択に倒す。
  // 一覧のページング・絞り込みではメンバーが欠けるため、選択に入れる対象は bundleId で引き直す
  const handleToggleBundleSelection = useCallback(
    async (record: ShipmentOrdersListExEntity) => {
      const { bundleId } = record
      if (bundleId === null) return

      // チェックの表示状態 (ShipmentOrdersTableBody) と同じ集計を使い、見た目と挙動を揃える
      const isFullySelected =
        buildBundleSelectionStates(dataSource, new Set(selectedRowKeys)).get(
          bundleId
        ) === 'all'

      if (isFullySelected) {
        setSelection((prev) => {
          if (prev.dcId !== search.dcId || prev.tenantKey !== tenantKey) {
            return prev
          }
          const orders = removeBundleFromSelection(prev.orders, bundleId)
          return orders === prev.orders ? prev : { ...prev, orders }
        })
        return
      }

      try {
        const bundle = await queryClient.fetchQuery(
          bundleOrdersQueryOptions({ tenantKey, bundleId })
        )
        // 選択を更新するタイミングで現在の文脈(拠点・テナント)を書き込み直すのは
        // handleSelectionChange と同じ方針
        setSelection((prev) => ({
          dcId: search.dcId,
          tenantKey,
          orders: addOrdersToSelection(
            prev.dcId === search.dcId && prev.tenantKey === tenantKey
              ? prev.orders
              : [],
            bundle.data
          )
        }))
      } catch (error) {
        toastApiErrorMessage(error)
      }
    },
    [
      dataSource,
      selectedRowKeys,
      search.dcId,
      tenantKey,
      queryClient,
      setSelection
    ]
  )

  const handleStatusChange = useCallback(
    async (id: number, deliveryStatus: ShipmentDeliveryStatusType) => {
      try {
        await bulkUpdateDeliveryStatus({
          tenantKey,
          ids: [id],
          deliveryStatus
        })
        message.success(SUCCESS_MESSAGES.UPDATED)
        // ピン留め行は絞り込み外に落ちていることがあり、その場合サーバー再取得では新しい
        // ステータスを取得できないため選択キャッシュ側も直接楽観更新しておく
        setSelection((prev) => {
          if (prev.dcId !== search.dcId || prev.tenantKey !== tenantKey) {
            return prev
          }
          return {
            ...prev,
            orders: prev.orders.map((order) =>
              order.id === id ? { ...order, deliveryStatus } : order
            )
          }
        })
        await onDataRefresh?.()
      } catch (error) {
        toastApiErrorMessage(error)
      }
    },
    [tenantKey, search.dcId, onDataRefresh, setSelection]
  )

  return (
    <FormProvider {...methods}>
      <div {...props}>
        <ShipmentOrdersTableHeaderActions
          tenantKey={tenantKey}
          authUser={authUser}
          search={search}
          pagination={pagination}
          onChangePage={onChangePage}
          selectedRowKeys={selectedRowKeys}
          clearSelection={clearSelection}
          selectedShipmentOrders={selectedOrders}
          isEditing={isEditing}
          setIdEditing={setIdEditing}
        />
        {pinnedOrders.length > 0 && (
          <div className="px-2 py-1 text-xs text-secondary">
            {DESCRIPTIONS.PINNED_SELECTED_SHIPMENT_ORDERS_NOTICE(
              pinnedOrders.length
            )}
          </div>
        )}
        <ShipmentOrdersTableBody
          tenantKey={tenantKey}
          shipmentOrders={dataSource}
          search={search}
          onChangeSort={onChangeSort}
          isEditing={isEditing}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          pinnedRowIds={pinnedRowIds}
          onStatusChange={handleStatusChange}
          statusEditVariant="clickable-tag"
          onClickOrderNo={setEditingShipmentOrder}
          onToggleBundleSelection={handleToggleBundleSelection}
          // 選択中は一括操作バーが上端に固定されるので、列ヘッダはその下に貼り付ける
          stickyOffsetHeader={
            selectedRowKeys.length > 0 ? SELECTION_OVERLAY_HEIGHT : 0
          }
        />
        <TableActions
          pageEntity={pagination}
          showPageSizeChanger
          onChangePage={onChangePage}
        />
      </div>
      {editingShipmentOrder && (
        <ShipmentOrderEditModal
          authUser={authUser}
          tenantKey={tenantKey}
          shipmentOrder={editingShipmentOrder}
          onClose={() => setEditingShipmentOrder(null)}
          onSuccess={async () => {
            setEditingShipmentOrder(null)
            await onDataRefresh?.()
          }}
        />
      )}
    </FormProvider>
  )
}
